import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const ARMED_OPTIONS = ['Armed', 'Unarmed'];
const GUARD_TYPE_OPTIONS = ['Security Guard', 'Law Enforcement'];
const INSIDE_OUTSIDE_OPTIONS = ['Inside', 'Outside'];

export const CreateGuardRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector(state => state.user.currentUser);

  const [armedUnarmed, setArmedUnarmed] = useState('');
  const [numberOfGuards, setNumberOfGuards] = useState('');
  const [guardType, setGuardType] = useState('');
  const [insideOutside, setInsideOutside] = useState('');
  const [schedule, setSchedule] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [contacts, setContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [showContactOptions, setShowContactOptions] = useState(false);
  const [showLocationOptions, setShowLocationOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isLoadingContacts, setIsLoadingContacts] = useState(true);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);

  const contactRef = useRef(null);
  const locationRef = useRef(null);

  const logoutUser = () => {
    dispatch({ type: 'LOG_OUT', payload: null });
    sessionStorage.setItem('currentUser', null);
    window.location = "/";
  };

  useEffect(() => {
    fetchContacts();
    fetchLocations();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contactRef.current && !contactRef.current.contains(e.target)) {
        setShowContactOptions(false);
      }
      if (locationRef.current && !locationRef.current.contains(e.target)) {
        setShowLocationOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch('/apiissueContacts', { credentials: 'include' });
      const data = await response.json();
      if (data && data.sessionExpired) { logoutUser(); return; }
      setContacts(data || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch('/apiissueLocations', { credentials: 'include' });
      const data = await response.json();
      if (data && data.sessionExpired) { logoutUser(); return; }
      setLocations(data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const missingRequiredData = () => !armedUnarmed || !selectedContact || !selectedLocation;

  const activeButMissingRequired = () => {
    const hasSome = armedUnarmed || numberOfGuards || guardType || insideOutside || schedule || additionalInfo || selectedContact || selectedLocation;
    const missingSome = !armedUnarmed || !selectedContact || !selectedLocation;
    return hasSome && missingSome;
  };

  const handleSubmit = async () => {
    if (missingRequiredData()) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/apicreateGuardRequest', {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({
          armedUnarmed,
          numberOfGuards,
          guardType,
          insideOutside,
          schedule,
          additionalInfo,
          relatedContact: selectedContact,
          relatedLocation: selectedLocation
        }),
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to submit guard request');
      }

      navigate('/', { state: { toast: 'Your guard request has been submitted successfully.' } });
    } catch (err) {
      setSubmitError(err.message || 'An error occurred while submitting the guard request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getContactFirstName = (contact) => contact[6] ? contact[6].value : '';
  const getContactLastName = (contact) => contact[8] ? contact[8].value : '';
  const getContactEmail = (contact) => contact[16] ? contact[16].value : '';
  const getContactRecordId = (contact) => contact[3] ? contact[3].value : '';

  const handleContactInputChange = (e) => {
    setContactSearch(e.target.value);
    setShowContactOptions(true);
    if (e.target.value === '') {
      setSelectedContact('');
    }
  };

  const handleContactSelect = (contact) => {
    const firstName = getContactFirstName(contact);
    const lastName = getContactLastName(contact);
    const email = getContactEmail(contact);
    setContactSearch(`${firstName} ${lastName} - ${email}`);
    setSelectedContact(getContactRecordId(contact));
    setShowContactOptions(false);
  };

  const getLocationCode = (location) => location[28] ? location[28].value : '';
  const getLocationAddress = (location) => location[8] ? location[8].value : '';
  const getLocationRecordId = (location) => location[3] ? location[3].value : '';

  const handleLocationInputChange = (e) => {
    setLocationSearch(e.target.value);
    setShowLocationOptions(true);
    if (e.target.value === '') {
      setSelectedLocation('');
    }
  };

  const handleLocationSelect = (location) => {
    const code = getLocationCode(location);
    const address = getLocationAddress(location);
    setLocationSearch(`${code} - ${address}`);
    setSelectedLocation(getLocationRecordId(location));
    setShowLocationOptions(false);
  };

  const filteredLocations = locations.filter(location => {
    const code = getLocationCode(location).toLowerCase();
    const address = getLocationAddress(location).toLowerCase();
    const search = locationSearch.toLowerCase();
    return code.includes(search) || address.includes(search);
  });

  const filteredContacts = contacts.filter(contact => {
    const firstName = getContactFirstName(contact).toLowerCase();
    const lastName = getContactLastName(contact).toLowerCase();
    const email = getContactEmail(contact).toLowerCase();
    const search = contactSearch.toLowerCase();
    return firstName.includes(search) || lastName.includes(search) || email.includes(search);
  });

  return (
    <div className="serviceTicket issueTypesPage">
      <h2>Submit a Guard Request</h2>
      <div className="serviceTicketWrapper">

        <div className="createTicketFormItem">
          <div className="labelAndStarWrapper">
            <p className="inputLabel">Armed/Unarmed</p>
            <p className="requiredStar">*</p>
          </div>
          <select value={armedUnarmed} onChange={(e) => setArmedUnarmed(e.target.value)}>
            <option value="">Select...</option>
            {ARMED_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div className="createTicketFormItem">
          <p className="inputLabel">Number of Guards</p>
          <input
            type="number"
            min="1"
            placeholder="Enter number of guards..."
            value={numberOfGuards}
            onChange={(e) => setNumberOfGuards(e.target.value)}
          />
        </div>

        <div className="createTicketFormItem">
          <p className="inputLabel">Guard Type</p>
          <select value={guardType} onChange={(e) => setGuardType(e.target.value)}>
            <option value="">Select...</option>
            {GUARD_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div className="createTicketFormItem">
          <p className="inputLabel">Inside/Outside</p>
          <select value={insideOutside} onChange={(e) => setInsideOutside(e.target.value)}>
            <option value="">Select...</option>
            {INSIDE_OUTSIDE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div className="createTicketFormItem" ref={contactRef}>
          <div className="labelAndStarWrapper">
            <p className="inputLabel">Related Client Contact</p>
            <p className="requiredStar">*</p>
          </div>
          <div className="autocompleteInput">
            <input
              type="text"
              value={contactSearch}
              onChange={handleContactInputChange}
              onFocus={() => setShowContactOptions(true)}
              placeholder={isLoadingContacts ? 'Loading contacts...' : 'Search contacts...'}
              disabled={isLoadingContacts}
            />
            {showContactOptions && filteredContacts.length > 0 && (
              <ul className="autocompleteSearchOptionsWrapper">
                {filteredContacts.map(contact => (
                  <li
                    className="autocompleteSearchItem"
                    key={getContactRecordId(contact)}
                    onClick={() => handleContactSelect(contact)}
                  >
                    <div className="autocompleteLocationCode">
                      <p style={{ fontWeight: 'bold' }}>Name</p>
                      <p>{getContactFirstName(contact)} {getContactLastName(contact)}</p>
                    </div>
                    <div className="autocompleteAddress">
                      <p style={{ fontWeight: 'bold' }}>Email</p>
                      <p>{getContactEmail(contact)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="createTicketFormItem" ref={locationRef}>
          <div className="labelAndStarWrapper">
            <p className="inputLabel">Related Client Location</p>
            <p className="requiredStar">*</p>
          </div>
          <div className="autocompleteInput">
            <input
              type="text"
              value={locationSearch}
              onChange={handleLocationInputChange}
              onFocus={() => setShowLocationOptions(true)}
              placeholder={isLoadingLocations ? 'Loading locations...' : 'Search locations...'}
              disabled={isLoadingLocations}
            />
            {showLocationOptions && filteredLocations.length > 0 && (
              <ul className="autocompleteSearchOptionsWrapper">
                {filteredLocations.map(location => (
                  <li
                    className="autocompleteSearchItem"
                    key={getLocationRecordId(location)}
                    onClick={() => handleLocationSelect(location)}
                  >
                    <div className="autocompleteLocationCode">
                      <p style={{ fontWeight: 'bold' }}>Location Code</p>
                      <p>{getLocationCode(location)}</p>
                    </div>
                    <div className="autocompleteAddress">
                      <p style={{ fontWeight: 'bold' }}>Address</p>
                      <p>{getLocationAddress(location)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="createTicketFormItem">
          <p className="inputLabel">Schedule</p>
          <textarea
            className="notesTextArea"
            placeholder="Enter schedule details..."
            value={schedule}
            onChange={(e) => setSchedule(e.target.value)}
          />
        </div>

        <div className="createTicketFormItem">
          <p className="inputLabel">Additional Information</p>
          <textarea
            className="notesTextArea"
            placeholder="Add any additional details..."
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
          />
        </div>

        {activeButMissingRequired() && (
          <p className="requiredInfoWarning">Please fill in all required fields before submitting *</p>
        )}

        {submitError && (
          <p className="requiredInfoWarning">{submitError}</p>
        )}

        <button
          className="mainButton"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Guard Request'}
        </button>
      </div>
    </div>
  );
};
